import { OrganizationalRecordDraftsProvider } from "./OrganizationalRecordDrafts";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Navigate, useParams } from "react-router";
import { useMutation, useQuery } from "@apollo/client/react";
import { Button } from "@/components/design/Button";
import { BoxHeader } from "@/components/design/BoxHeader";
import { SegmentedControl } from "@/components/design/SegmentedControl";
import { CreateOrganizationalRecordDocument, OrganizationalRecordDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { OrganizationalRecordDocuments } from "./OrganizationalRecordDocuments";
import { OrganizationalRecordEntries } from "./OrganizationalRecordEntries";
import { OrganizationalRecordComments } from "./OrganizationalRecordComments";
import { useOrganizationalRecordAction } from "./use-organizational-record-action";
import { OrganizationalRecordItemDialog } from "./OrganizationalRecordItemDialog";
import type { OrganizationalRecordReference } from "./OrganizationalRecordLinks";
import type { OrganizationalRecordAccess } from "./use-organizational-record-access";

export function OrganizationalRecordPage() {
  const { id = "" } = useParams();
  return <Navigate to={`/p/${id}#organisationsakte`} replace />;
}

export function OrganizationalRecordPanel({ personId, access }: { personId: string; access: OrganizationalRecordAccess }) {
  if (!access.canRead) return null;
  return <OrganizationalRecord key={personId} personId={personId} canWrite={access.canWrite} />;
}

function OrganizationalRecord({ personId, canWrite }: { personId: string; canWrite: boolean }) {
  const intl = useIntl();
  const query = useQuery(OrganizationalRecordDocument, { variables: { personId }, fetchPolicy: "no-cache" });
  const [create] = useMutation(CreateOrganizationalRecordDocument, { fetchPolicy: "no-cache" });
  const action = useOrganizationalRecordAction();
  const [tab, setTab] = useState<"documents" | "entries" | "discussion">("documents");
  const [linkedItem, setLinkedItem] = useState<OrganizationalRecordReference | null>(null);
  // Inspect the destination above the current section, preserving the source and its drafts.
  const navigate = (item: OrganizationalRecordReference) => setLinkedItem(item);
  const organizationalRecord = query.data?.organizationalRecord;
  return <div className="box space-y-6 bg-surface p-6">
    <div>
      <BoxHeader title={<FormattedMessage id="organizationalRecord.heading" />} />
      <p className="max-w-[42rem] field-value text-ink/55"><FormattedMessage id="organizationalRecord.description" /></p>
      {organizationalRecord && !query.error && <SegmentedControl className="mt-4" value={tab} onChange={setTab}
        aria-label={intl.formatMessage({ id: "organizationalRecord.heading" })}
        options={(["documents", "entries", "discussion"] as const).map(value => ({ value, label: <FormattedMessage id={`organizationalRecord.${value}`} /> }))} />}
    </div>
    {(query.error || action.error) && <p role="alert">{action.error ?? userErrorMessage(intl, query.error)}</p>}
    {query.loading && <p><FormattedMessage id="common.loading" defaultMessage="Loading…" /></p>}
    {!query.loading && !query.error && !organizationalRecord && <div className="space-y-4">
      <p><FormattedMessage id="organizationalRecord.empty" /></p>
      {canWrite && <Button disabled={action.busy} onClick={() => void action.run(async () => {
        await create({ variables: { personId } }); await query.refetch();
      })}><FormattedMessage id="organizationalRecord.create" /></Button>}
    </div>}
    {!query.error && organizationalRecord && <OrganizationalRecordDraftsProvider key={organizationalRecord.id} recordId={organizationalRecord.id} canWrite={canWrite}>
      <div hidden={tab !== "documents"}><OrganizationalRecordDocuments key={organizationalRecord.id} recordId={organizationalRecord.id} canWrite={canWrite} onNavigate={navigate} embedded /></div>
      {tab === "entries" && <OrganizationalRecordEntries recordId={organizationalRecord.id} canWrite={canWrite} onNavigate={navigate} embedded />}
      {tab === "discussion" && <OrganizationalRecordComments recordId={organizationalRecord.id} canWrite={canWrite} onNavigate={navigate} embedded />}
      {linkedItem && <OrganizationalRecordItemDialog key={`${linkedItem.kind}:${linkedItem.id}`} recordId={organizationalRecord.id} item={linkedItem}
        canWrite={canWrite} onClose={() => setLinkedItem(null)} onNavigate={navigate} />}
    </OrganizationalRecordDraftsProvider>}
  </div>;
}
